// ANGEL FLIGHT — App: renderer host + single rAF loop + screen routing.
//
// Owns the GameView3D (renderer/scene/camera), ONE requestAnimationFrame loop,
// the DOM UI layer (menus via the Router + the in-game FLIR HUD), and the flow:
// Menu → Hangar → Game → Debrief, with Pause as an overlay. The save is the
// single source of truth threaded through every screen.

import { AudioBus } from '../audio/AudioBus';
import { CrewComms } from '../audio/CrewComms';
import { createEventBus } from '../core/EventBus';
import { createRng } from '../core/rng';
import { loadSave, writeSave } from '../core/storage';
import type { Difficulty, GameSettings, MissionResult, SaveStateV1, WeaponId } from '../core/types';
import { getMission, MISSIONS, missionIndexOf } from '../missions/index';
import { Platform } from '../platform';
import { isMissionUnlocked } from '../progression';
import type { GameContext } from '../systems/System';
import { CameraRig } from '../render/CameraRig';
import { ColorWorld } from '../render/ColorWorld';
import { preloadEnemyAssets } from '../render/EnemyAssets';
import { GameView3D } from '../render/GameView3D';
import { GunView3D } from '../render/GunView3D';
import { HudOverlay } from '../render/HudOverlay';
import { Juice3D } from '../render/Juice3D';
import { TouchControls } from '../render/TouchControls';
import { CINEMATIC } from '../config/cinematic';
import { GameLoop } from './GameLoop';
import { IntroCinematic } from './IntroCinematic';
import { Router } from './Router';
import { BriefingScreen } from './screens/BriefingScreen';
import { DebriefScreen } from './screens/DebriefScreen';
import { HangarScreen } from './screens/HangarScreen';
import { MenuScreen } from './screens/MenuScreen';
import { PauseOverlay } from './screens/PauseOverlay';

export class App {
  private readonly parent: HTMLElement;
  private readonly uiLayer: HTMLDivElement;
  private readonly view: GameView3D;
  private readonly gun: GunView3D;
  private readonly router: Router;
  private readonly hud: HudOverlay;
  private readonly touch: TouchControls;
  private readonly audio: AudioBus;
  private readonly comms: CrewComms;
  private readonly save: SaveStateV1;
  private loop: GameLoop | null = null;
  private cameraRig: CameraRig | null = null;
  /** Live intro cinematic (campaign opener only; null in normal play). */
  private cinematic: IntroCinematic | null = null;
  /** Mission-less ColorWorld running behind the menus (G2 attract mode). */
  private attract: ColorWorld | null = null;
  /** Bound to the live mission bus so the gun recoils on each shot. */
  private gunKick: ((p: { recoil: number }) => void) | null = null;
  private rafId = 0;
  private lastMs = 0;
  private running = false;
  private paused = false;

  constructor(parent: HTMLElement) {
    this.parent = parent;
    this.save = loadSave();

    // The 3D canvas sits at the back; a transparent DOM layer holds the UI on top.
    this.view = new GameView3D(parent);
    // The foreground gun viewmodel — built once, rendered as an overlay by the
    // view, swung/recoiled per frame while a mission is live.
    this.gun = new GunView3D();
    this.gun.init();
    this.view.setGun(this.gun);
    // Warm up the rigged enemy GLBs so the horde spawns as real soldiers (not
    // capsules) — non-blocking; capsule fallback covers the load window.
    void preloadEnemyAssets();
    this.uiLayer = document.createElement('div');
    Object.assign(this.uiLayer.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none', // screens re-enable on their own interactive bits
    } as CSSStyleDeclaration);
    parent.appendChild(this.uiLayer);

    this.router = new Router(this.uiLayer);
    this.hud = new HudOverlay(this.uiLayer);
    this.hud.hide();
    // Touch-first command layer: weapon rack + PAUSE + FLIR, usable without a
    // keyboard (its root is pointer-events:none; only the buttons re-enable).
    this.touch = new TouchControls(this.uiLayer, {
      onWeapon: (id) => this.selectWeapon(id),
      onPause: () => this.pause(),
      onFlir: () => this.toggleFlir(),
      onFlare: () => this.deployFlares(),
    });
    this.audio = new AudioBus();
    this.comms = new CrewComms();
    this.registerScreens();

    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('blur', this.onBlur);
    // Browsers/iOS gate audio behind a user gesture — unlock once, then detach.
    window.addEventListener('pointerdown', this.onFirstGesture);
    this.onResize();

    // The rAF loop runs continuously (it also renders the live thermal scene
    // behind the menus); start it now.
    this.startRaf();
  }

  // ── screen registry + navigation ────────────────────────────────────────

  private registerScreens(): void {
    this.router.register('menu', () =>
      new MenuScreen({
        save: this.save,
        // Picking a mission opens its BRIEFING first — the authored radio brief
        // (and the CHECK-YOUR-FIRE rule) renders before the DEPLOY confirm.
        onDeploy: (missionId) => this.router.show('briefing', missionId),
        onArmory: () => this.router.show('hangar'),
        // Menu replay — straight into the campaign opener with the intro forced.
        onReplayIntro: () =>
          this.startMission(CINEMATIC.missionId, { intro: true, forceIntro: true }),
      }),
    );
    this.router.register('briefing', () =>
      new BriefingScreen({
        save: this.save,
        // Deploys from the briefing carry the intro cinematic (the campaign
        // opener only — startMission gates on CINEMATIC.missionId). Debrief
        // RETRY + ?dev= quickstarts deploy without it.
        onDeploy: (missionId) => this.startMission(missionId, { intro: true }),
        onBack: () => this.router.show('menu'),
      }),
    );
    this.router.register('hangar', () =>
      new HangarScreen({ save: this.save, onBack: () => this.router.show('menu') }),
    );
    this.router.register('pause', () =>
      new PauseOverlay({
        settings: this.save.settings,
        difficulty: this.save.difficulty,
        flirOn: this.view.getMode() === 'thermal',
        onResume: () => this.resume(),
        onMainMenu: () => this.abortToMenu(),
        onSettingsChange: (s) => this.applySettings(s),
        onDifficultyChange: (d) => this.applyDifficulty(d),
        onFlirToggle: (on) => this.toggleFlir(on),
      }),
    );
  }

  /** Show the main menu (entry point). */
  showMenu(): void {
    this.teardownMission();
    this.startAttract();
    this.hud.hide();
    this.uiLayer.style.pointerEvents = 'auto';
    this.router.show('menu');
    // The quiet menu pad runs only outside missions (no-op until unlocked).
    this.audio.startMenuTheme();
  }

  // ── menu attract world ────────────────────────────────────────────────────

  /** A live battlefield behind the menus: a mission-less ColorWorld (scrolling
   *  asphalt, dusk sky, burning skyline, props sliding past) driven by a stub
   *  context with an EMPTY enemy list. The menu becomes a window into the war
   *  instead of a black void (the audit's words). Themed to the NEXT mission. */
  private startAttract(): void {
    if (this.attract || this.loop) return;
    const next =
      MISSIONS[Math.min(this.save.missionProgress, MISSIONS.length - 1)] ?? MISSIONS[0]!;
    // ColorWorld only reaches ctx.view / bus / mission / renderRng / enemies —
    // the sim-side fields (gunner, run, stats…) are never touched without a
    // GameLoop, so the stub narrows honestly through the cast.
    const ctx = {
      view: this.view,
      bus: createEventBus(),
      rng: createRng(1),
      renderRng: createRng(2),
      difficulty: this.save.difficulty,
      ownedWeapons: this.save.ownedWeapons,
      mission: next,
      enemies: [],
    } as unknown as GameContext;
    this.attract = new ColorWorld();
    this.attract.init(ctx);
  }

  private stopAttract(): void {
    this.attract?.destroy();
    this.attract = null;
  }

  // ── mission lifecycle ───────────────────────────────────────────────────

  startMission(missionId: string, opts: { intro?: boolean; forceIntro?: boolean } = {}): void {
    // Secondary gate (the MenuScreen unlock wall is the primary one): a locked
    // campaign mission can NEVER deploy. Endless (missionIndexOf → -1) + the free
    // tier + every mission while unlockAll is on are unlocked, so this no-ops for
    // those and refuses only a genuinely-walled mission (the SDK now gates by
    // default, so a locked mission is hard-refused even before config loads).
    if (!isMissionUnlocked(missionIndexOf(missionId))) {
      this.showMenu();
      return;
    }
    this.teardownMission();
    this.stopAttract(); // the mission's own ColorWorld takes the scene
    // Intro plays on briefing deploys into the campaign opener — ONCE per save
    // (CINEMATICS.md rule). The menu's REPLAY INTRO forces it back on.
    const wantIntro =
      opts.intro === true &&
      CINEMATIC.enabled &&
      missionId === CINEMATIC.missionId &&
      (opts.forceIntro === true || !this.save.introSeen);

    const cameraRig = new CameraRig();
    cameraRig.configure({
      screenshake: this.save.settings.screenshake,
      hitstop: this.save.settings.hitstop,
    });
    this.cameraRig = cameraRig;

    this.loop = new GameLoop({
      missionId,
      save: this.save,
      view: this.view,
      worldRenderer: new ColorWorld(),
      renderSystems: [cameraRig, new Juice3D()],
      isFrozen: () => cameraRig.frozen,
      onResolved: (result) => this.onMissionResolved(result),
    });

    // Recoil the gun on every shot.
    this.gunKick = (p: { recoil: number }) => this.gun.kick(p.recoil);
    this.loop.bus.on('weapon:fired', this.gunKick);

    this.router.clear(); // drop menus; the scene + HUD own the screen now
    this.uiLayer.style.pointerEvents = 'none'; // canvas takes the input
    this.hud.bind(this.loop.bus, this.loop.gunner.maxHull);
    // The objective module's identity for this deploy: a plain-words label
    // ("HOLD THE LINE") + the duration; the HUD calls it out on first show so
    // the player always knows WHAT they're doing before wave one.
    const missionDef = getMission(missionId);
    if (missionDef.objective === 'endless') {
      // Last Stand: a count-up "time survived" timer, no deadline.
      this.hud.setObjective('LAST STAND', 0, true);
    } else {
      this.hud.setObjective(
        missionDef.objective === 'survive' ? 'HOLD THE LINE' : 'KILL THE BOSS',
        missionDef.durationMs,
      );
    }
    this.touch.bind(this.loop.bus);
    this.touch.setOwned(this.save.ownedWeapons);
    this.touch.setActive(this.loop.gunner.activeWeapon);
    this.touch.setFlir(this.view.getMode() === 'thermal');
    this.audio.setVolumes(this.save.settings);
    this.audio.bind(this.loop.bus);
    // Crew chatter director: authored radio lines off the same bus (bind AFTER
    // the audio so its squelch listener is live for the first line).
    this.comms.bind(this.loop.bus);
    // The HUD bound AFTER the systems init'd — re-announce weapon + ammo so the
    // readouts are honest BEFORE the first shot (no more 'AMMO —').
    this.loop.announceHud();
    // Pre-compile every shader (world + gun overlay) so the first trigger pull
    // doesn't hitch on a synchronous GPU program build (~710ms on the audit).
    this.view.warmup();
    this.paused = false;

    if (wantIntro) {
      // Hold the sim (world keeps scrolling under the rAF loop); HUD + touch
      // stay hidden until the hand-off. Skip and full-play land identically.
      this.loop.introHold = true;
      this.cinematic = new IntroCinematic({
        root: this.uiLayer,
        view: this.view,
        gun: this.gun,
        gunner: this.loop.gunner,
        onDone: () => this.endCinematic(),
      });
    } else {
      this.hud.show();
      this.touch.show();
    }
  }

  /** Cinematic hand-off — releases the sim and reveals the combat UI. Runs on
   *  natural finish AND on skip (the cinematic restores gun/FLIR state first). */
  private endCinematic(): void {
    this.cinematic = null;
    if (!this.save.introSeen) {
      this.save.introSeen = true; // full play AND skip both count as seen
      writeSave(this.save);
    }
    if (!this.loop) return;
    this.loop.introHold = false;
    this.touch.setFlir(this.view.getMode() === 'thermal');
    this.hud.show();
    this.touch.show();
  }

  /** Touch FLARE button → the same InputSystem.deployFlares key F reaches.
   *  Gated like other combat input: live mission only, not paused, not while
   *  the intro holds the sim. */
  private deployFlares(): void {
    if (!this.loop || this.loop.isResolved || this.paused || this.cinematic) return;
    this.loop.deployFlares();
  }

  /** Touch weapon select: set the gunner's intent; FiringSystem swaps to that
   *  gun's own magazine next tick and announces `weapon:switched`. */
  private selectWeapon(id: WeaponId): void {
    if (!this.loop || this.loop.isResolved) return;
    if (!this.save.ownedWeapons.includes(id)) return;
    this.loop.gunner.activeWeapon = id;
  }

  /** Flip the colour/FLIR render pipeline at runtime (key T, the touch button,
   *  or the pause toggle). `force` pins a state; absent = toggle. */
  private toggleFlir(force?: boolean): void {
    const want = force ?? this.view.getMode() === 'color';
    this.view.setMode(want ? 'thermal' : 'color');
    this.touch.setFlir(want);
  }

  private onMissionResolved(result: MissionResult): void {
    this.hud.unbind();
    this.hud.hide();
    this.touch.unbind();
    this.touch.hide();
    // Brief beat on the cleared/failed banner, then route to Debrief.
    window.setTimeout(() => {
      this.teardownMission();
      this.startAttract(); // keep a live world behind the debrief
      this.uiLayer.style.pointerEvents = 'auto';
      this.router.show('debrief', result);
    }, 900);
    // Register the debrief factory lazily so it captures this result.
    this.router.register('debrief', () =>
      new DebriefScreen({
        result,
        // The wallet AFTER banking (endMission wrote the save before this
        // callback) — the debrief shows the run's pay landing somewhere real.
        wallet: { scrip: this.save.scrip, salvage: this.save.salvage },
        onContinue: () => this.showMenu(),
        onArmory: () => this.router.show('hangar'),
        // One-tap re-deploy into the same mission (no menu round-trip).
        onRetry: () => this.startMission(result.missionId),
      }),
    );
  }

  private teardownMission(): void {
    // A mission abort while the intro runs tears the overlay down WITHOUT the
    // hand-off callback (no loop to release).
    this.cinematic?.destroy();
    this.cinematic = null;
    this.hud.unbind();
    this.touch.unbind();
    this.touch.hide();
    this.comms.unbind();
    this.audio.unbind();
    if (this.gunKick && this.loop) this.loop.bus.off('weapon:fired', this.gunKick);
    this.gunKick = null;
    this.loop?.destroy();
    this.loop = null;
    this.cameraRig = null;
  }

  // ── pause ───────────────────────────────────────────────────────────────

  private pause(): void {
    if (!this.loop || this.paused || this.loop.isResolved) return;
    this.paused = true;
    this.uiLayer.style.pointerEvents = 'auto';
    this.router.showOverlay('pause');
  }

  private resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.router.clearOverlay();
    this.uiLayer.style.pointerEvents = 'none';
  }

  private abortToMenu(): void {
    this.paused = false;
    this.router.clearOverlay();
    this.showMenu();
  }

  private applySettings(s: GameSettings): void {
    this.save.settings = s;
    writeSave(this.save);
    this.cameraRig?.configure({ screenshake: s.screenshake, hitstop: s.hitstop });
    this.audio.setVolumes(s);
  }

  /** Persist a difficulty change (resolved into stats on the NEXT deploy). */
  private applyDifficulty(d: Difficulty): void {
    this.save.difficulty = d;
    writeSave(this.save);
  }

  // ── loop + events ─────────────────────────────────────────────────────────

  private startRaf(): void {
    if (this.running) return;
    this.running = true;
    this.lastMs = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  }

  private readonly tick = (nowMs: number): void => {
    this.rafId = requestAnimationFrame(this.tick);
    const delta = nowMs - this.lastMs;
    this.lastMs = nowMs;
    const dtMs = Math.min(delta, 50);
    if (!this.paused) this.loop?.update(delta);
    // Play-to-earn drip: tick the shared-economy playtime clock ONLY while a live
    // mission is actively running — not paused, not resolved, and not during the
    // intro cinematic hold (presentation only). The SDK self-throttles + daily-
    // caps; it's a no-op when unlockAll/anon. dt in seconds, capped (no huge jump
    // on a backgrounded tab).
    if (this.loop && !this.paused && !this.loop.isResolved && !this.loop.introHold && !this.cinematic) {
      Platform.tickPlaytime(dtMs / 1000);
    }
    // Intro cinematic cue list (frozen alongside the loop while paused).
    if (this.cinematic && !this.paused) this.cinematic.update(dtMs);
    // The attract world scrolls behind the menus when no mission is live.
    if (!this.loop && this.attract) this.attract.update(dtMs);
    if (this.loop) {
      // Reticle follows the live aim point (same coords FiringSystem raycasts
      // from → crosshair, bullets and hits agree).
      this.hud.update(this.loop.gunner.aim, dtMs);
      // The gun viewmodel swings toward aim + recoils + spins up.
      this.gun.update(dtMs / 1000, this.loop.gunner);
      // Crew chatter clock (flushes queued urgent lines once the gap clears).
      if (!this.paused) this.comms.update(dtMs);
    }
    this.view.render(dtMs / 1000);
  };

  private readonly onResize = (): void => {
    this.view.resize(this.parent.clientWidth, this.parent.clientHeight);
  };

  private readonly onKey = (e: KeyboardEvent): void => {
    // Any key skips the intro cinematic (no preventDefault — browser keys stay
    // functional; skip lands in the identical hand-off state). Stop the event
    // here so the InputSystem's own keydown (F = flares, 1-4 = weapons —
    // registered AFTER this handler) can't fire combat actions mid-cinematic.
    if (this.cinematic) {
      e.stopImmediatePropagation();
      if (!e.repeat) this.cinematic.skip();
      return;
    }
    if (e.key === 'Escape' && this.loop && !this.loop.isResolved) {
      if (this.paused) this.resume();
      else this.pause();
    }
    // FLIR toggle — the signature sensor flip, live mid-mission.
    if (e.code === 'KeyT' && this.loop && !this.loop.isResolved) {
      this.toggleFlir();
    }
  };

  private readonly onBlur = (): void => {
    if (this.loop && !this.loop.isResolved) this.pause();
  };

  /** One-time audio unlock on the first user gesture (browsers/iOS require it). */
  private readonly onFirstGesture = (): void => {
    window.removeEventListener('pointerdown', this.onFirstGesture);
    this.audio.unlock();
  };

  destroy(): void {
    cancelAnimationFrame(this.rafId);
    this.running = false;
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('pointerdown', this.onFirstGesture);
    this.teardownMission();
    this.stopAttract();
    this.router.destroy();
    this.hud.destroy();
    this.touch.destroy();
    this.comms.destroy();
    this.audio.destroy();
    this.view.dispose();
  }
}
